import React from 'react';
import { useTranslation } from 'react-i18next';
import { BaseChart } from './base-chart.component';
import {
  ErrorsChartProps,
  RpmChartProps,
  ResponseTimeChartProps,
  RevenueChartProps
} from '../../types/charts.types';
import { IResponseTimeMetric } from '../../types/metrics.types';

export const ErrorsChart = ({
  data,
  isLoading,
  isError,
  onRefresh
}: ErrorsChartProps) => {
  const { t } = useTranslation();
  const chartData = React.useMemo(
    () => ({
      labels: data?.map((d) => d.date) || [],
      datasets: [
        {
          label: t('admin.charts.series.errorsPerMinute'),
          data: data?.map((d) => d.errors_per_minute) || [],
          borderColor: 'rgba(255, 99, 132, 0.8)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)'
        },
        {
          label: t('admin.charts.series.errorCount'),
          data: data?.map((d) => d.error_count) || [],
          borderColor: 'rgba(220, 38, 38, 0.8)',
          backgroundColor: 'rgba(220, 38, 38, 0.5)'
        }
      ]
    }),
    [data, t]
  );

  return (
    <BaseChart
      title={t('admin.charts.titles.errors')}
      {...chartData}
      isLoading={isLoading}
      isError={isError}
      onRefresh={onRefresh}
    />
  );
};

export const ResponseTimeChart = ({
  data,
  isLoading,
  isError,
  onRefresh
}: ResponseTimeChartProps) => {
  const { t } = useTranslation();
  const chartData = React.useMemo(() => {
    if (!data) return { labels: [], datasets: [] };

    const allDates = new Set<string>();
    Object.values(data).forEach((metricData) => {
      (metricData as IResponseTimeMetric[]).forEach((item) =>
        allDates.add(item.date)
      );
    });
    const labels = Array.from(allDates).sort();

    return {
      labels,
      datasets: [
        {
          label: t('admin.charts.series.webTransaction'),
          data: labels.map((date) => {
            const item = (data.HttpDispatcher || []).find((d) => d.date === date);
            return item ? item.average_response_time : 0;
          }),
          borderColor: 'rgba(117, 187, 49, 0.8)',
          backgroundColor: 'rgba(117, 187, 49, 0.5)'
        },
        {
          // "Prisma" is the ORM's name — verbatim.
          label: 'Prisma',
          data: labels.map((date) => {
            const item = (data['Datastore/Prisma/allWeb'] || []).find(
              (d) => d.date === date
            );
            return item ? item.average_response_time : 0;
          }),
          borderColor: 'rgba(232, 192, 81, 0.8)',
          backgroundColor: 'rgba(232, 192, 81, 0.5)'
        },
        {
          label: t('admin.charts.series.responseTime'),
          data: labels.map((date) => {
            const item = (data.WebTransactionTotalTime || []).find(
              (d) => d.date === date
            );
            return item ? item.average_response_time : 0;
          }),
          borderColor: 'rgba(5, 63, 121, 0.8)',
          backgroundColor: 'rgba(5, 63, 121, 0.5)',
          fill: false
        }
      ]
    };
  }, [data, t]);

  return (
    <BaseChart
      title={t('admin.charts.titles.responseTime')}
      {...chartData}
      isLoading={isLoading}
      isError={isError}
      onRefresh={onRefresh}
    />
  );
};

export const RpmChart = ({ data, isLoading, isError, onRefresh }: RpmChartProps) => {
  const { t } = useTranslation();
  const chartData = React.useMemo(
    () => ({
      labels: data?.map((d) => d.date) || [],
      datasets: [
        {
          label: t('admin.charts.series.requests'),
          data: data?.map((d) => d.requests_per_minute) || [],
          borderColor: 'rgba(232, 192, 81, 0.8)',
          backgroundColor: 'rgba(232, 192, 81, 0.5)'
        }
      ]
    }),
    [data, t]
  );

  return (
    <BaseChart
      title={t('admin.charts.titles.rpm')}
      {...chartData}
      isLoading={isLoading}
      isError={isError}
      onRefresh={onRefresh}
    />
  );
};

export const RevenueChart = ({
  data,
  isLoading,
  isError,
  onRefresh
}: RevenueChartProps) => {
  const { t } = useTranslation();
  const chartData = React.useMemo(
    () => ({
      labels: data?.map((d) => d.date) || [],
      datasets: [
        {
          label: t('admin.charts.series.total'),
          data: data?.map((d) => d.total) || [],
          borderColor: 'rgba(5, 63, 121, 0.8)',
          backgroundColor: 'rgba(5, 63, 121, 0.5)'
        }
      ]
    }),
    [data, t]
  );

  return (
    <BaseChart
      title={t('admin.charts.titles.revenue')}
      {...chartData}
      isLoading={isLoading}
      isError={isError}
      onRefresh={onRefresh}
    />
  );
};
